import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, Moon, ShieldAlert } from 'lucide-react';
import { API_URL } from '../config/api';

const MAX_CHARS = 500;

const SUGGESTIONS = [
  'How do I add a new service?',
  "Where do I see today's bookings?",
  'What is the Chartroom?',
  'How do promo codes work?',
];

const toneClasses = (tone) => {
  if (tone === 'warning') return 'bg-amber-50 border border-amber-200 text-amber-900';
  if (tone === 'alert') return 'bg-red-50 border border-red-200 text-red-900';
  return 'bg-slate-100 text-slate-800';
};

const Bubble = ({ role, tone, children, streaming }) => (
  <div className={`flex items-end gap-2 ${role === 'user' ? 'justify-end' : 'justify-start'}`}>
    {role !== 'user' && (
      <img src="/bosun-avatar.png" alt="Bosun" className="h-6 w-6 rounded-full bg-slate-100 shrink-0 mb-0.5" />
    )}
    <div
      className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        role === 'user' ? 'bg-[#1F2A5C] text-white rounded-br-md' : `${toneClasses(tone)} rounded-bl-md`
      }`}
    >
      {children}
      {streaming && <span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-[#F5B92E] animate-pulse rounded-sm" />}
    </div>
  </div>
);

const minutesUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms > 0 ? Math.ceil(ms / 60000) : 0;
};

/**
 * Ask Bosun — the maritime AI help chat, opened from the ✨ sparkle box.
 * Streams answers from POST /api/v2/bosun/chat (SSE). Rate-limited server-side
 * (10 hails/hour → "nightwatch" cooldown, memory wiped between watches).
 */
const AskBosun = ({ open, onClose, currentView }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  // mode: 'ok' | 'nightwatch' | 'disabled'
  const [mode, setMode] = useState('ok');
  const [resetAt, setResetAt] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/v2/bosun/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
      });
      if (!res.ok) return;
      const s = await res.json();
      setRemaining(s.remaining);
      if (!s.enabled) {
        setMode('disabled');
        setMessages([]);
      } else if (s.remaining <= 0) {
        setMode('nightwatch');
        setResetAt(s.resetAt);
        setMessages([]); // memory wiped between watches
      } else {
        setMode('ok');
      }
    } catch (e) { /* status is best-effort */ }
  }, []);

  // Scroll to the newest message (bottom) whenever messages change or panel opens
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      fetchStatus();
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        if (inputRef.current) inputRef.current.focus();
      }, 60);
    }
  }, [open, fetchStatus]);

  // Nightwatch countdown — when it hits zero, refresh status (memory already wiped)
  useEffect(() => {
    if (mode !== 'nightwatch' || !resetAt) return;
    const tick = () => {
      const m = minutesUntil(resetAt);
      setCountdown(m);
      if (m === 0) fetchStatus();
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, [mode, resetAt, fetchStatus]);

  if (!open) return null;

  const send = async (text) => {
    const message = (text || input).trim().slice(0, MAX_CHARS);
    if (!message || streaming || mode !== 'ok') return;
    setInput('');
    const history = messages.slice(-10).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: message }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    const appendToLast = (chunk) =>
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + chunk };
        return next;
      });
    const setLast = (content, tone) =>
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content, tone };
        return next;
      });

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 90000);
      const res = await fetch(`${API_URL}/v2/bosun/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
        },
        body: JSON.stringify({ message, history, context: currentView || null }),
        signal: controller.signal,
      });
      if (res.status === 429) {
        clearTimeout(timer);
        const data = await res.json().catch(() => ({}));
        setMode('nightwatch');
        setResetAt(data?.detail?.resetAt || null);
        setMessages([]); // wipe memory for the next watch
        return;
      }
      if (res.status === 403) {
        clearTimeout(timer);
        setMode('disabled');
        setMessages([]);
        return;
      }
      if (!res.ok || !res.body) {
        clearTimeout(timer);
        setLast("Bosun couldn't be reached — please try again in a moment.");
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split('\n\n');
        buf = frames.pop();
        for (const frame of frames) {
          if (!frame.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(frame.slice(6));
            if (evt.type === 'delta') appendToLast(evt.text);
            else if (evt.type === 'warning') setLast(evt.message, 'warning');
            else if (evt.type === 'disabled') { setLast(evt.message, 'alert'); setMode('disabled'); }
            else if (evt.type === 'error') setLast(evt.message);
          } catch (e) { /* skip malformed frame */ }
        }
      }
      clearTimeout(timer);
      setRemaining((r) => (typeof r === 'number' ? Math.max(0, r - 1) : r));
    } catch (e) {
      setLast("Bosun couldn't be reached — please check your connection and try again.");
    } finally {
      setStreaming(false);
    }
  };

  const inputDisabled = streaming || mode !== 'ok';

  return (
    <>
      <div className="fixed inset-0 z-[69]" onClick={onClose} data-testid="ask-bosun-overlay" />
      <div
        data-testid="ask-bosun-panel"
        className="fixed right-3 top-1/2 z-[70] -translate-y-1/2 flex flex-col w-[min(92vw,400px)] h-[min(76vh,600px)] rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-[#1F2A5C] px-4 py-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <img
              src="/bosun-avatar.png"
              alt="Bosun"
              data-testid="ask-bosun-avatar"
              className={`h-9 w-9 rounded-full bg-white/10 ring-2 ring-[#F5B92E]/60 ${mode === 'disabled' ? 'grayscale opacity-60' : ''}`}
            />
            <span>
              <span className="block text-sm font-bold text-white leading-tight">Ask Bosun</span>
              <span className="block text-[11px] text-blue-200 leading-tight">
                {mode === 'nightwatch' ? 'On nightwatch' : mode === 'disabled' ? 'Off duty' : 'Your first mate for BookingFlows'}
              </span>
            </span>
          </div>
          <button onClick={onClose} aria-label="Close Ask Bosun" data-testid="ask-bosun-close" className="rounded-lg p-1.5 text-blue-200 hover:bg-white/10 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        {mode === 'disabled' ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center" data-testid="ask-bosun-disabled">
            <img src="/bosun-avatar.png" alt="Bosun" className="h-16 w-16 rounded-full grayscale opacity-50 mb-4" />
            <ShieldAlert className="h-5 w-5 text-red-400 mb-2" />
            <p className="text-sm font-semibold text-slate-700 mb-1">Bosun has been relieved of duty</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              Ask Bosun is unavailable for your organization pending a review by BookingFlows.
              If you believe this is an error, contact support@bookingflows.com.
            </p>
          </div>
        ) : mode === 'nightwatch' ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center" data-testid="ask-bosun-nightwatch">
            <div className="relative mb-4">
              <img src="/bosun-avatar.png" alt="Bosun" className="h-16 w-16 rounded-full opacity-70" />
              <Moon className="absolute -right-1 -bottom-1 h-6 w-6 text-[#1F2A5C] bg-white rounded-full p-1 shadow" />
            </div>
            <p className="text-sm font-semibold text-slate-700 mb-1">Bosun is on nightwatch</p>
            <p className="text-xs text-slate-500 leading-relaxed mb-2">
              Ten bells an hour is the limit, sailor. Bosun's resting up
              {countdown != null && countdown > 0 ? ` — back on deck in about ${countdown} minute${countdown === 1 ? '' : 's'}.` : ' — back on deck shortly.'}
            </p>
            <p className="text-[10px] text-slate-400">The slate is wiped clean each watch.</p>
          </div>
        ) : (
          <div ref={scrollRef} data-testid="ask-bosun-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 ? (
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <img src="/bosun-avatar.png" alt="Bosun" className="h-8 w-8 rounded-full" />
                  <p className="text-sm text-slate-600 font-semibold">Ahoy! I'm Bosun.</p>
                </div>
                <p className="text-xs text-slate-500 mb-4">Ask me anything about running your operation on BookingFlows — I'll point you to the right deck.</p>
                <div className="space-y-2">
                  {SUGGESTIONS.map((sug, i) => (
                    <button
                      key={sug}
                      data-testid={`ask-bosun-suggestion-${i}`}
                      onClick={() => send(sug)}
                      className="block w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-medium text-slate-700 transition-colors hover:border-[#1F2A5C]/30 hover:bg-[#EBF2FF]"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <Bubble key={i} role={m.role} tone={m.tone} streaming={streaming && i === messages.length - 1 && m.role === 'assistant'}>
                  {m.content}
                </Bubble>
              ))
            )}
          </div>
        )}

        {/* Input */}
        <div className="border-t border-slate-100 p-3 shrink-0">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              data-testid="ask-bosun-input"
              value={input}
              maxLength={MAX_CHARS}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={mode === 'ok' ? 'Ask Bosun anything…' : 'Bosun is off watch'}
              disabled={inputDisabled}
              className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#1F2A5C] focus:bg-white focus:outline-none disabled:opacity-60 transition-colors"
            />
            <button
              data-testid="ask-bosun-send"
              onClick={() => send()}
              disabled={inputDisabled || !input.trim()}
              aria-label="Send message"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1F2A5C] text-white transition-colors hover:bg-[#28356f] disabled:opacity-40"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-1.5 flex items-center justify-between px-0.5">
            <p className="text-[10px] text-slate-400">
              {typeof remaining === 'number' && remaining <= 3 && mode === 'ok'
                ? `${remaining} bell${remaining === 1 ? '' : 's'} left this watch`
                : 'Bosun answers about BookingFlows only.'}
            </p>
            {input.length > MAX_CHARS - 120 && (
              <span className="text-[10px] text-slate-400" data-testid="ask-bosun-char-count">{input.length}/{MAX_CHARS}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AskBosun;
